import React, { useState, useEffect, useContext } from "react";
import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import {
    GridApi,
    ICellRendererParams,
    ITooltipParams,
    ValueFormatterParams,
} from "ag-grid-community";
import cronstrue from "cronstrue";
import Layout from "@wrappers/layouts/main-layout/Layout";
import RulesFilterModal from "@modals/rules-modal/RulesFilterModal";
import RulesSummaryModal from "@modals/rules-modal/RulesSummaryModal";
import {
    FrequencyCellRenderer,
    NameCellRenderer,
    FilterCellRenderer,
    RowActionButtons,
    ParseCronString
} from "@cell-renderers/AllCellRenderers";
import { useClearGridData, useGetModulesData } from "@hooks/AllCustomHooks";
import { setIsLoader } from "@features/loaderSlice";
import { useDispatch, useSelector } from "react-redux";
import { mainContext } from "@ts/constants";
import { titleCase, populateRules } from "@ts/utils";
import { updateTitle } from "@features/titleSlice";
import { updateActiveModule } from "@features/activeModuleSlice";
import * as Sentry from "@sentry/react";
import AddRulesModal from "@modals/rules-modal/AddRulesModal";
import { IChannel, IMainContext, Rule, TActiveClientId, TClientTimezone } from "@ts/common-types";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const Rules: React.FC = () => {
    const { setRulesFilterDisp, cerbosPermissions } =
        useContext<IMainContext>(mainContext);
    const [gridApi, setGridApi] = useState<GridApi>();
    const clearGridData = useClearGridData();
    const [rowData, setRowData] = useState<Rule[]>();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(true);
    const channel = useSelector((state: IChannel) => state.channel.value);
    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );
    const clientTimezone = useSelector(
        (state: TClientTimezone) => state.clientTimezone.value
    );

    useEffect(() => {
        clearGridData();
        dispatch(updateTitle("Rules"));
        dispatch(updateActiveModule("rules"));
        dispatch(setIsLoader(true));
        setLoading(true);

        populateRules()
            .then((rules) => {
                setRowData(rules as Array<Rule>);
                setLoading(false);
                dispatch(setIsLoader(false));
            })
            .catch((err) => {
                console.error(err);
                Sentry.captureException(err);
            });

        // eslint-disable-next-line
    }, [activeClientId, channel, dispatch]);

    const rulesGridStyles = {
        height: "100%",
        width: "100%",
        maxHeight: "calc(100% - 92px)",
    };

    const columnDefs = useGetModulesData({
        type: "columns",
        colDefs: [
            {
                field: "rule_id",
                headerName: "#",
                maxWidth: 75,
                enableCellChangeFlash: true,
                pinned: true,
            },
            {
                field: "name",
                headerName: "Name",
                cellRenderer: NameCellRenderer,

                maxWidth: 320,
                tooltipValueGetter: ({ value }: { value: string }) => value,
                enableCellChangeFlash: true,
                pinned: true,
                wrapText: true,
            },
            {
                field: "type",
                headerName: "Type",

                valueFormatter: ({ value }: { value: string }) => titleCase(value),
                maxWidth: 80,
                enableCellChangeFlash: true,
            },
            {
                field: "filter",
                headerName: "Filters",
                cellRenderer: (params: ICellRendererParams) =>
                    FilterCellRenderer(params, setRulesFilterDisp),
                cellClass: ["d-flex", "justify-content-center"],
                sortable: false,

                wrapText: true,
                maxWidth: 500,
                enableCellChangeFlash: true,
                hide: true,
                filter: false,
            },
            {
                headerName: "Module",
                field: "module",

                valueFormatter: ({ value }: { value: string }) => titleCase(value),
                enableCellChangeFlash: true,
                maxWidth: 110,
                pinned: true,
            },
            {
                headerName: "Schedule",
                field: "cron_string",
                tooltipValueGetter: (params: ITooltipParams) => {
                    try {
                        if (
                            params.data.frequency_value ||
                            params.data.type === "query"
                        ) {
                            return FrequencyCellRenderer(params);
                        } else if (params.value) {
                            return cronstrue.toString(params.value);
                        } else {
                            return "nil";
                        }
                    } catch (err) {
                        console.error(err);
                        Sentry.captureException(err);
                    }
                },
                cellRenderer: ParseCronString,

                minWidth: 166,
                enableCellChangeFlash: true,
            },
            {
                field: "created_at",
                headerName: "Added On",
                valueFormatter: (params: ValueFormatterParams) =>
                    dayjs
                        .utc(params.value)
                        .tz(clientTimezone as unknown as string)
                        .format("YYYY-MM-DD HH:mm:ss"),
                // tooltipValueGetter: ({ value }) => value,
                cellClass: "text-center",
                enableCellChangeFlash: true,
                hide: true,
            },
            {
                field: "created_by",
                headerName: "Added By",

                tooltipValueGetter: ({ value }: { value: string }) => value,
                cellClass: "text-center",
                enableCellChangeFlash: true,
                hide: true,
                filter: false,
            },
            {
                field: "updated_at",
                headerName: "Updated On",
                valueFormatter: (params: ValueFormatterParams) =>
                    dayjs
                        .utc(params.value)
                        .tz(clientTimezone as unknown as string)
                        .format("YYYY-MM-DD HH:mm:ss"),
                tooltipValueGetter: ({ value }: { value: string }) => value,
                enableCellChangeFlash: true,
                hide: true,
                filter: false,
            },
            {
                field: "updated_by",
                headerName: "Updated",

                tooltipValueGetter: ({ value }: { value: string }) => value,
                enableCellChangeFlash: true,
                hide: true,
                filter: false,
            },
            {
                field: "status",
                headerName: "Status",
                valueFormatter: ({ value }: { value: string }) => titleCase(value),
                enableCellChangeFlash: true,
            },
            {
                field: "",
                headerName: "",
                equals: () => false,
                cellRenderer: RowActionButtons,
                filter: false,
            },
        ]
    });

    return (
        <>
            <Layout>
                <div className="main-content-body">
                    <div
                        id="rules-grid"
                        className="ag-theme-alpine"
                        style={rulesGridStyles}
                    >
                        <AgGridLayout
                            rowData={rowData}
                            columnDefs={columnDefs}
                            setGridApi={setGridApi}
                            loading={loading}
                            isTotalRowsReq={false}
                        ></AgGridLayout>
                    </div>
                </div>
                <RulesFilterModal api={gridApi} />
                <AddRulesModal api={gridApi} />
                <RulesSummaryModal />
            </Layout>
        </>
    );
};

export default Rules;
